import React, { useEffect, useRef } from "react";
import { Modal, CircularProgress } from "@mui/material";
import { renderAsync } from "docx-preview";
import { LoaderWrapper } from "./Styled";
import { ButtonRow as ModalButtonRow, ModalWrapper, Expandable, TitleText } from "../../../widgets/MaterialOrder/single/AddMaterialModal/Styled";
import { StyledButton as ModalCancelButton } from "../../../widgets/MaterialOrder/Styled";

export interface DocxPreviewModalProps {
  open: boolean;
  onClose: () => void;
  blob: Blob | null;
  title?: string;
  BackdropComponent?: any;
  backdropProps?: Record<string, unknown>;
  debug?: boolean;
  actions?: React.ReactNode;
  download?: () => void;
  loadingError?: string | null;
  onRetry?: () => void;
}

export const DocxPreviewModal: React.FC<DocxPreviewModalProps> = ({
  open,
  onClose,
  blob,
  title = "Xem trước tài liệu",
  BackdropComponent,
  backdropProps,
  debug = false,
  actions,
  download,
  loadingError,
  onRetry,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const renderWithRetry = async (attempt: number = 0) => {
      if (!open || !blob || !containerRef.current) return;
      const container = containerRef.current as HTMLDivElement;
      const { clientWidth, clientHeight } = container;
      if ((clientWidth === 0 || clientHeight === 0) && attempt < 20) {
        if (debug) console.debug("DocxPreviewModal: container not ready", { attempt, clientWidth, clientHeight });
        setTimeout(() => renderWithRetry(attempt + 1), 50);
        return;
      }
      try {
        if (isCancelled) return;
        if (debug) console.debug("DocxPreviewModal: rendering", { size: blob.size, type: blob.type, attempt });
        container.innerHTML = "";
        await renderAsync(blob, container, undefined, { className: "docx" });
        if (debug) console.debug("DocxPreviewModal: rendered");
      } catch (e) {
        console.error("DocxPreviewModal: error rendering DOCX", e);
      }
    };

    if (containerRef.current) {
      containerRef.current.innerHTML = "";
    }
    const t = setTimeout(() => renderWithRetry(0), 0);
    return () => {
      isCancelled = true;
      clearTimeout(t);
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [open, blob, debug]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={BackdropComponent}
      BackdropProps={backdropProps}
    >
      <ModalWrapper isOpen={open} onClick={(e) => e.stopPropagation()}>
        <Expandable>
          <TitleText>{title}</TitleText>
          {blob ? (
            <div ref={containerRef as any} />
          ) : loadingError ? (
            <LoaderWrapper style={{ flexDirection: "column", gap: 16 }}>
              <div>{loadingError}</div>
              {onRetry && (
                <ModalCancelButton variant="contained" onClick={onRetry}>
                  Thử lại
                </ModalCancelButton>
              )}
            </LoaderWrapper>
          ) : (
            <LoaderWrapper>
              <CircularProgress />
            </LoaderWrapper>
          )}
        </Expandable>
        <ModalButtonRow>
          <ModalCancelButton variant="outlined" onClick={onClose}>Đóng</ModalCancelButton>
          {download && <ModalCancelButton variant="contained" onClick={download}>Tải phiếu</ModalCancelButton>}
          {actions}
        </ModalButtonRow>
      </ModalWrapper>
    </Modal>
  );
};

export default DocxPreviewModal;


